// src/services/CommunicationsService.ts
//
// Single unified WebSocket service for both messaging and notifications.
// Replaces the former MessagingService + NotificationService pair which opened
// two separate Pusher connections to the same Reverb server and collided on
// the shared private-user.{id} channel.
//
// Key design principles:
//  - ONE Pusher connection shared by all consumers.
//  - Namespaced listener groups: each consumer registers under its own key so
//    handlers never overwrite each other.
//  - connect() is idempotent — safe to call from multiple contexts; it only
//    creates a new socket when the userId changes.
//  - disconnect() is safe to call on an already-closed socket.
//  - Components/hooks must NOT call disconnect() — only the auth logout flow should.

import { ENV } from '../config/runtimeEnv';
import Pusher from 'pusher-js';
import {
	MessageSentEvent,
	MessageReadEvent,
	TypingIndicatorEvent,
	ConversationAssignedEvent,
	ParticipantAddedEvent,
	ParticipantRemovedEvent,
	ConversationQueuedEvent,
	ConversationClosedEvent,
} from '../types/messaging';
import type { UserUuid } from '../types/identity';
import { http } from '../lib/http/client';
import { getAuthSession, subscribeAuthSession } from '../lib/http/session';
import { CLOSE_REAUTHENTICATE, CLOSE_SERVICE_RESTART, closeCodeOf, confirmSessionBeforeReconnect, reauthenticateAfterClose, type ReauthOutcome } from '../lib/realtime/reauth';

// Reverb is reached on the page's own host through the gateway (contract §19); there is no absolute WebSocket URL.
const NOTIFICATION_WS_PATH = '/ws/notification';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'failed';

export interface CommunicationNotification {
	id: string;
	user_id: string;
	type: 'alert' | 'info' | 'error' | 'warning';
	title: string;
	message: string;
	related_entity_id?: string;
	read_status: boolean;
	status: string;
	auto_delete?: boolean;
	category?: string | null;
	created_at: string;
	updated_at: string;
}

export interface CommunicationsEvents {
	// ── Notification events (private-user.{id} channel) ──
	onNotificationReceived: (notification: CommunicationNotification) => void;
	onNotificationStatusChanged: (notification: Partial<CommunicationNotification>) => void;

	// ── Messaging: user-level events (private-user.{id} channel) ──
	onAddedToConversation: (event: ParticipantAddedEvent) => void;
	onRemovedFromConversation: (event: ParticipantRemovedEvent) => void;
	onConversationClosed: (event: ConversationClosedEvent) => void;

	// ── Messaging: queue events (private-clinic.queue channel) ──
	onQueueConversationAdded: (event: ConversationQueuedEvent) => void;
	onConversationAssigned: (event: ConversationAssignedEvent) => void;

	// ── Messaging: conversation events (private-conversation.{id} channel) ──
	onMessageSent: (event: MessageSentEvent) => void;
	onMessageRead: (event: MessageReadEvent) => void;
	onTypingIndicator: (event: TypingIndicatorEvent) => void;
	onParticipantAdded: (event: ParticipantAddedEvent) => void;
	onParticipantRemoved: (event: ParticipantRemovedEvent) => void;

	// ── Connection ──
	onConnectionStateChanged: (state: ConnectionState) => void;
}

// ─── Service ──────────────────────────────────────────────────────────────────

class CommunicationsService {
	private pusher: Pusher | null = null;
	private userChannel: any = null;
	private queueChannel: any = null;
	private conversationChannels: Map<string, any> = new Map();

	private userId: UserUuid | null = null;
	private connectionState: ConnectionState = 'disconnected';

	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	// While the session is re-checked after a gateway close, pusher-js's own "disconnected" must not schedule a reconnect.
	private recovering = false;
	// Open conversations survive a reconnect for the same user: re-subscribed once the new socket is up.
	private pendingConversations: string[] = [];
	private reconnectAttempts = 0;
	private readonly maxReconnectAttempts = 10;
	private readonly reconnectDelay = 1000;

	// Namespaced listeners — multiple consumers never overwrite each other
	private listenerGroups: Map<string, Partial<CommunicationsEvents>> = new Map();

	constructor() {
		// A session that ends in this tab (logout, revocation, a failed refresh) closes the socket it opened.
		subscribeAuthSession((next, previous) => {
			if (next.status === 'anonymous' && previous.status === 'authenticated' && this.userId) this.disconnect();
		});
		if (typeof window !== 'undefined') {
			window.addEventListener('beforeunload', () => this.disconnect());
			document.addEventListener('visibilitychange', () => {
				if (
					!document.hidden &&
					this.userId &&
					this.connectionState !== 'connected' &&
					this.connectionState !== 'connecting'
				) {
					this.reconnectAttempts = 0;
					this.scheduleReconnect();
				}
			});
		}
	}

	// ─── Listener registration ────────────────────────────────────────────────

	/** Register a named group of event handlers. Replaces any previous group with the same name. */
	registerListeners(namespace: string, handlers: Partial<CommunicationsEvents>): void {
		this.listenerGroups.set(namespace, handlers);
	}

	/** Remove a named group of event handlers. */
	unregisterListeners(namespace: string): void {
		this.listenerGroups.delete(namespace);
	}

	private dispatch<K extends keyof CommunicationsEvents>(event: K, data: any): void {
		this.listenerGroups.forEach((handlers) => {
			const handler = handlers[event];
			if (handler) (handler as (d: typeof data) => void)(data);
		});
	}

	// ─── Connection management ────────────────────────────────────────────────

	/**
	 * Connect to the WebSocket server.
	 * Safe to call from multiple contexts — only creates a new Pusher instance when
	 * userId changes. The clinic-global queue channel is subscribed on connect for the staff audience.
	 */
	async connect(userId: UserUuid): Promise<void> {
		// Each clinic has its own Reverb key (contract §22); there is no shared default to fall back to.
		const appKey = ENV.VITE_REVERB_APP_KEY;
		if (!appKey) {
			console.error('VITE_REVERB_APP_KEY is not configured');
			return;
		}

		// Already connected for this user — nothing further to do
		if (
			this.userId === userId &&
			(this.connectionState === 'connected' || this.connectionState === 'connecting')
		) {
			return;
		}

		const carried = this.userId === userId ? Array.from(this.conversationChannels.keys()) : [];
		this.disconnect();
		this.userId = userId;
		this.pendingConversations = carried;

		this.setConnectionState('connecting');

		try {
			const apiUrl = ENV.VITE_NOTIFICATION_BASE_URL || '/api/notification';

			const secure = window.location.protocol === 'https:';
			const port = Number(window.location.port) || (secure ? 443 : 80);

			this.pusher = new Pusher(appKey, {
				wsHost: window.location.hostname,
				wsPort: port,
				wssPort: port,
				wsPath: ENV.VITE_NOTIFICATION_WS_PATH || NOTIFICATION_WS_PATH,
				enabledTransports: ['ws', 'wss'],
				forceTLS: secure,
				disableStats: true,
				cluster: 'mt1',
				activityTimeout: 120000,
				pongTimeout: 60000,
				unavailableTimeout: 10000,

				authorizer: (channel: any) => ({
					authorize: async (socketId: string, callback: any) => {
						try {
							// The shared client sends the session cookie and refreshes once on a 401 (contract §19).
							const res = await http.post(
								`${apiUrl}/broadcasting/authentication`,
								{ socket_id: socketId, channel_name: channel.name },
								{ headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } },
							);
							callback(null, res.data);
						} catch (err) {
							console.error('[CommunicationsService] Channel auth failed:', { status: (err as { response?: { status?: number } })?.response?.status });
							callback(err, null);
						}
					},
				}),
			});

			this.pusher.connection.bind('connected', () => {
				this.setConnectionState('connected');
				this.reconnectAttempts = 0;
				this.subscribeToUserChannel();
				this.subscribeToQueueChannel();
				this.pendingConversations.splice(0).forEach((id) => this.subscribeToConversation(id));
			});

			this.pusher.connection.bind('disconnected', () => {
				if (this.connectionState !== 'failed') {
					this.setConnectionState('disconnected');
				}
				if (!this.recovering) this.scheduleReconnect();
			});

			this.pusher.connection.bind('error', (error: any) => {
				const code = closeCodeOf(error);
				// pusher-js treats every code from 4300 up as "refused" and stops, so a 4401 is answered here (contract §19).
				if (code === CLOSE_REAUTHENTICATE) {
					this.recover(reauthenticateAfterClose, true);
					return;
				}
				if (code === 1006 || code === undefined || code === CLOSE_SERVICE_RESTART) {
					console.warn(`[CommunicationsService] WS dropped (code ${code ?? 'unknown'}), reconnecting…`);
				} else {
					console.error('[CommunicationsService] WS error:', { code, type: error?.type });
				}
				this.setConnectionState('failed');
			});

			// An upgrade refused with 401 is invisible to the browser, so the session is confirmed before the next attempt.
			this.pusher.connection.bind('unavailable', () => {
				this.setConnectionState('failed');
				this.recover(confirmSessionBeforeReconnect, false);
			});
		} catch (err) {
			console.error('[CommunicationsService] Failed to init connection:', err);
			this.setConnectionState('failed');
		}
	}

	// ─── User channel ─────────────────────────────────────────────────────────

	private subscribeToUserChannel(): void {
		if (!this.pusher || !this.userId || this.userChannel) return;

		const ch = this.pusher.subscribe(`private-user.${this.userId}`);
		this.userChannel = ch;

		// Notification events
		ch.bind('notification.received', (data: CommunicationNotification) =>
			this.dispatch('onNotificationReceived', data)
		);
		ch.bind('notification.status.changed', (data: Partial<CommunicationNotification>) =>
			this.dispatch('onNotificationStatusChanged', data)
		);

		// Messaging user-level events
		ch.bind('participant.added', (data: ParticipantAddedEvent) =>
			this.dispatch('onAddedToConversation', data)
		);
		ch.bind('participant.removed', (data: ParticipantRemovedEvent) =>
			this.dispatch('onRemovedFromConversation', data)
		);
		ch.bind('conversation.closed', (data: ConversationClosedEvent) =>
			this.dispatch('onConversationClosed', data)
		);

		ch.bind('pusher:subscription_error', (err: any) =>
			console.error('[CommunicationsService] User channel error:', err)
		);
	}

	// ─── Queue channel ────────────────────────────────────────────────────────

	private subscribeToQueueChannel(): void {
		// notification signs the queue channel for the staff audience only (contract §19); read per call — the singleton outlives logins.
		if (!this.pusher || this.queueChannel || getAuthSession().info?.session?.audience !== 'staff') return;

		const ch = this.pusher.subscribe(`private-clinic.queue`);
		this.queueChannel = ch;

		ch.bind('conversation.assigned', (data: ConversationAssignedEvent) =>
			this.dispatch('onConversationAssigned', data)
		);
		ch.bind('conversation.queued', (data: ConversationQueuedEvent) =>
			this.dispatch('onQueueConversationAdded', data)
		);
		ch.bind('pusher:subscription_error', (err: any) =>
			console.error('[CommunicationsService] Queue channel error:', err)
		);
	}

	// ─── Conversation channels ────────────────────────────────────────────────

	subscribeToConversation(conversationId: string): void {
		if (!this.pusher || this.conversationChannels.has(conversationId)) return;

		const ch = this.pusher.subscribe(`private-conversation.${conversationId}`);

		ch.bind('message.sent', (data: MessageSentEvent) => this.dispatch('onMessageSent', data));
		ch.bind('message.read', (data: MessageReadEvent) => this.dispatch('onMessageRead', data));
		ch.bind('typing.updated', (data: TypingIndicatorEvent) => this.dispatch('onTypingIndicator', data));
		ch.bind('participant.added', (data: ParticipantAddedEvent) => this.dispatch('onParticipantAdded', data));
		ch.bind('participant.removed', (data: ParticipantRemovedEvent) => this.dispatch('onParticipantRemoved', data));
		ch.bind('conversation.assigned', (data: ConversationAssignedEvent) => this.dispatch('onConversationAssigned', data));
		ch.bind('conversation.closed', (data: ConversationClosedEvent) => this.dispatch('onConversationClosed', data));
		ch.bind('pusher:subscription_error', (err: any) => {
			console.error(`[CommunicationsService] Conversation channel error (${conversationId}):`, err);
			// Remove the failed channel so a subsequent subscribeToConversation call can retry.
			ch.unbind_all();
			this.conversationChannels.delete(conversationId);
			// Retry once after a short delay if we are still connected.
			setTimeout(() => {
				if (this.connectionState === 'connected' && this.pusher) {
					this.subscribeToConversation(conversationId);
				}
			}, 2000);
		});

		this.conversationChannels.set(conversationId, ch);
	}

	unsubscribeFromConversation(conversationId: string): void {
		const ch = this.conversationChannels.get(conversationId);
		if (ch) {
			ch.unbind_all();
			this.safeUnsubscribe(ch.name);
			this.conversationChannels.delete(conversationId);
		}
	}

	/** Returns true when the user currently has this conversation channel open. */
	isConversationSubscribed(conversationId: string): boolean {
		return this.conversationChannels.has(conversationId);
	}

	getActiveConversations(): string[] {
		return Array.from(this.conversationChannels.keys());
	}

	// ─── Disconnect ───────────────────────────────────────────────────────────

	/** Full teardown — only call on logout, never from component cleanup. */
	disconnect(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		this.conversationChannels.forEach((ch) => {
			ch.unbind_all();
			this.safeUnsubscribe(ch.name);
		});
		this.conversationChannels.clear();

		if (this.queueChannel) {
			this.queueChannel.unbind_all();
			this.safeUnsubscribe(this.queueChannel.name);
			this.queueChannel = null;
		}

		if (this.userChannel) {
			this.userChannel.unbind_all();
			this.safeUnsubscribe(this.userChannel.name);
			this.userChannel = null;
		}

		if (this.pusher) {
			try {
				const state = this.pusher.connection.state;
				if (state !== 'disconnected' && state !== 'failed') {
					this.pusher.disconnect();
				}
			} catch {
				// Ignore errors from already-closed sockets
			}
			this.pusher = null;
		}

		this.userId = null;
		this.pendingConversations = [];
		this.setConnectionState('disconnected');
	}

	// ─── Session recovery ─────────────────────────────────────────────────────

	// After a 4401 the refreshed session reconnects at once; anything else keeps the backoff, since Reverb itself may be down.
	private recover(check: () => Promise<ReauthOutcome>, reconnectNow: boolean): void {
		if (this.recovering || !this.userId) return;
		this.recovering = true;
		const userId = this.userId;
		void check().then((outcome) => {
			this.recovering = false;
			if (this.userId !== userId) return;
			if (outcome === 'signed_out') {
				this.disconnect();
				return;
			}
			if (outcome === 'reconnect' && reconnectNow) {
				this.reconnectAttempts = 0;
				this.setConnectionState('disconnected');
				void this.connect(userId);
				return;
			}
			this.setConnectionState('failed');
			this.scheduleReconnect();
		});
	}

	// ─── Helpers ──────────────────────────────────────────────────────────────

	/** Only call pusher.unsubscribe when the socket is actually open. */
	private safeUnsubscribe(channelName: string): void {
		if (!this.pusher) return;
		try {
			if (this.pusher.connection.state === 'connected') {
				this.pusher.unsubscribe(channelName);
			}
		} catch {
			// Ignore errors from already-closed sockets
		}
	}

	private scheduleReconnect(): void {
		if (this.reconnectTimer || !this.userId) return;

		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.error('[CommunicationsService] Max reconnects reached');
			this.setConnectionState('failed');
			return;
		}

		if (typeof document !== 'undefined' && document.hidden) return;

		const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);
		this.reconnectAttempts++;

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			if (this.userId) this.connect(this.userId);
		}, delay);
	}

	private setConnectionState(state: ConnectionState): void {
		if (this.connectionState !== state) {
			this.connectionState = state;
			this.dispatch('onConnectionStateChanged', state);
		}
	}

	getConnectionState(): ConnectionState {
		return this.connectionState;
	}

	isConnected(): boolean {
		return this.connectionState === 'connected';
	}

	/**
	 * Returns the current Pusher socket ID, used as X-Socket-ID header
	 * so Laravel's broadcast()->toOthers() can exclude the sender.
	 */
	getSocketId(): string | null {
		try {
			return this.pusher?.connection?.socket_id ?? null;
		} catch {
			return null;
		}
	}
}

const communicationsService = new CommunicationsService();
export default communicationsService;
